import React from "react";
import { useTheme } from "../../context/ThemeContext";
import { getMutedText } from "../../utils/themeUtils";

interface FeeBreakdownProps {
  amount: number;
  paymentType: "card" | "ach";
}

/**
 * Displays a processing fee surcharge breakdown when the tenant has enabled
 * fee passthrough for the selected payment method.
 * Reads settings from localStorage (populated on app load).
 */
const FeeBreakdown: React.FC<FeeBreakdownProps> = ({ amount, paymentType }) => {
  const { isDarkMode } = useTheme();

  if (!amount || amount <= 0) return null;

  const storedSettings = localStorage.getItem("settings");
  if (!storedSettings) return null;

  let settings: any;
  try {
    settings = JSON.parse(storedSettings);
  } catch {
    return null;
  }

  const isCardFeeEnabled = paymentType === "card" && settings?.pass_card_fee_to_customer;
  const isAchFeeEnabled = paymentType === "ach" && settings?.pass_ach_fee_to_customer;

  if (!isCardFeeEnabled && !isAchFeeEnabled) return null;

  const feePercent = paymentType === "card"
    ? parseFloat(settings?.credit_card_fee) || 0
    : parseFloat(settings?.ach_fee) || 0;

  if (feePercent <= 0) return null;

  const surcharge = Math.round(amount * (feePercent / 100) * 100) / 100;
  const total = amount + surcharge;
  const feeLabel = paymentType === "card" ? "Card processing fee" : "ACH processing fee";

  return (
    <div className={`p-3 rounded-lg border text-sm ${
      isDarkMode
        ? "bg-blue-900/20 border-blue-800 text-blue-200"
        : "bg-blue-50 border-blue-200 text-blue-900"
    }`}>
      <div className="space-y-1">
        <div className="flex justify-between">
          <span className={getMutedText(isDarkMode)}>Subtotal</span>
          <span>${amount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className={getMutedText(isDarkMode)}>{feeLabel} ({feePercent}%)</span>
          <span>${surcharge.toFixed(2)}</span>
        </div>
        <div className={`flex justify-between font-semibold pt-1 border-t ${
          isDarkMode ? "border-blue-800" : "border-blue-200"
        }`}>
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default FeeBreakdown;
